/** @module SiteAdmin/RoleBasedPaths */
define([
	"dojo/_base/config",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/store/Memory",
	"csi",
	"csi/_base/underscore",
	"csi/core/PathHints",
	"csi/config/preferences",
	"csi/core/ExtendedPath",
	"csi/def",
	"csi/def/Action",
	"csi/def/Actions",
	"csi/def/Toolbar",
	"csi/expander/Widget",
	"csi/heading/BooleanController",
	"csi/heading/TextController",
	"csi/locale",
	"csi/reflection",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/store/Nodes",
	"csi/view/ToolbarPluginSelect",
	"csi/xml",
	"./Locale",
	"./ToolbarPluginLocalizedItem"
], function(config, declare, lang, Memory, csi, _, PathHints, configPreferences, ExtendedPath, def, Action, Actions, Toolbar, Widget, BooleanController, TextController,
	locale, reflection, sessionConstants, {localStore, sessionStore}, Nodes, ToolbarPluginSelect, xml, Locale, ToolbarPluginLocalizedItem) {

	var ACTION_ABSTRACT = "Resolve Abstract Path";
	var ATTR_PATHNAME = "PathName";
	var ATTR_PATHNAME_INHERITED = "PathNameInherited";
	var ATTR_TARGET = "Target";
	var ATTR_VISIBLE = "Visible";
	var MAX_LEVEL = 7;

	var ABSTRACT_TYPES_WITH_PATHS = [
		"DataSheet"
	];

	var _pathHints;
	var _selectedType;
	var _typeStore;

	/**
	 * Return list of nodes that represent relations that can be traversed under given node.
	 * @param {Context} ctx
	 * @param {Boolean} bConfigurable
	 * @return {Node[]}
	 */
	function _getStore(ctx, bConfigurable) {
		// do not allow going deeper than specified max
		var node = ctx.getNode();
		var data = node._data;
		var level = data ? data.level : 0;
		var parent = data ? node.$URL : csi.EMPTY_URL;
		var type = data ? node[ATTR_TARGET] : _selectedType;
		var previousPath = data ? data.path : new ExtendedPath(type);
		var previousRelationId = data ? data.relation : null;
		var rc = reflection.getClass(type);

		var children = _.reduce(rc.getRelations(true), function(memo, rcr) {
			// only enabled, non-hidden, and single destination relations are shown
			var rcDest = _getValidRelationDestination(rc, rcr, previousRelationId);
			if (rcDest) {
				var id = rcr.getId();
				var target = rcDest.getType();
				var name = (id == xml.ATTR_PARENT) ? locale.type(target) : locale.typeId(type, id);
				var child = ctx.cache.createClientNode(null, null, name);
				var path = previousPath.clone().addRelationStep(id, target);

				// track extra meta-data for node, including level in tree, extended path, and relation id
				child._data = {
					level: level + 1,
					path: path,
					relation: id
				};

				// populate attributes to support view
				xml.nodeCreateClientAttr(child, xml.ATTR_PARENT, xml.ATTR_REF, parent);
				xml.nodeCreateClientAttr(child, ATTR_TARGET, xml.ATTR_STRING, target, xml.ATTR_FLAG_NOEDIT);

				// do not allow editing path names for matrix paths
				if (path.isSingle) {
					_setPathName(child);
				}

				_setVisible(child);

				memo.push(child);
			}

			return memo;
		}, []);

		children = xml.sortNodes(children);

		xml.nodeCreateClientAttr(node, "Children", xml.ATTR_REFVECTOR, _.map(children, xml.nodeGetURL));

		return new Nodes({
			cachingStore: ctx.cache,
			data: children
		});
	}

	/**
	 * Return store of domain types that can appear in views.
	 * @return {Memory} memory store of all types that can appear in views.
	 */
	function _getTypeStore() {
		if (!_typeStore) {
			var domainTypes = _.pluck(reflection.getDomainClasses(), "type");
			var items = _.reduce(domainTypes.concat(ABSTRACT_TYPES_WITH_PATHS), function(memo, type) {
				if (_isValidType(type, true)) {
					memo.push({
						id: type,
						name: locale.type(type)
					});
				}

				return memo;
			}, []);

			_typeStore = new Memory({
				data: csi.sort(items, "name")
			});
		}

		return _typeStore;
	}

	/**
	 * Return true if relation from given class points to a destination class that is valid.
	 * @param {Class} rc - reflection class that is source of relation
	 * @param {Relation} rcr - reflection relation
	 * @param {string} [previousRelationId] - indicates relation that was traversed to get to this relation
	 * @return {boolean}
	 */
	function _getValidRelationDestination(rc, rcr, previousRelationId) {
		if (!rcr.isEnabled || rcr.isHidden) {
			return null;
		}

		if (!rcr.isSingle && !rcr.isViableMatrix) {
			return null;
		}

		if (previousRelationId && (previousRelationId === rcr.bidirectionalRelation?.getId())) {
			return null;
		}

		// handle special case to not return to master when coming from it
		if ((rcr.getId() === xml.ATTR_PARENT) && ((previousRelationId === reflection.ATTR_CURRENT_REVISION) || (previousRelationId === reflection.ATTR_LATEST_REVISION))) {
			return null;
		}

		var rcDest = rcr.getDestinationClass(rc);
		if (!rcDest || !_isValidType(rcDest.getType(), false)) {
			return null;
		}

		return rcDest;
	}

	/**
	 * Return true if there are valid relations from current node and we are not at the max level of the table.
	 * @param {Context} ctx
	 * @return {boolean}
	 */
	function _hasChildren(ctx) {
		var node = ctx.getNode();
		var data = node._data;
		if (!data) {
			return true;
		}

		// don't allow traversing more than fixed number of levels
		if (data.level == MAX_LEVEL) {
			return false;
		}

		return _isValidType(node[ATTR_TARGET], true, data.relation);
	}

	/**
	 * Return true if reflection class has valid relations to show.
	 * @param {Class} rc - reflection class
	 * @param {string} [previousRelationId] - indicates relation used to get to this class, which plays a part
	 * in deciding if relation is valid. We should not allow to traverse reverse relation of previous relation (i.e. Child:Attributes/Child:__Parent__)
	 * @return {boolean}
	 */
	function _hasValidRelations(rc, previousRelationId) {
		// for now, do not allow expanding a User relation as it will only lead to recursive users (due to ModifiedBy)
		if (rc.getType() == xml.TYPE_USER) {
			return false;
		}

		return _.some(rc.getRelations(true), function(rcr) {
			return !!_getValidRelationDestination(rc, rcr, previousRelationId);
		});
	}

	/**
	 * Return true if Preference Path Management applies to given type.
	 * @param {string} type - domain class type
	 * @param {boolean} [bCheckRelations] - check that type has at least one valid relation to show
	 * @param {string} [previousRelationId] - id of relation traversed to get to this type
	 * @return {boolean}
	 */
	function _isValidType(type, bCheckRelations, previousRelationId) {
		var rc = reflection.getClass(type);
		if (rc.disabled) {
			return false;
		}

		// revision classes are not currently their own business objects, but they are domain objects,
		// so allow here
		if (rc.isDerivedFrom("_CS_Revision")) {
			return true;
		}

		// FIX: This misses "Color" from PartMaterialColor.PMCColor
		if (!rc.getAlias()) {
			return false;
		}

		if (rc.isDerivedFrom("ClassConfiguration") ||
			rc.isDerivedFrom("Subtype") ||
			rc.isDerivedFrom("TableReport")) {
			return false;
		}

		if (bCheckRelations && !_hasValidRelations(rc, previousRelationId)) {
			return false;
		}

		return true;
	}

	/**
	 * Call given callback function for given node and all its children.
	 * @param {Node} node
	 * @param {Cache} cache
	 * @param {Function} callback - function to call for each node
	 * @param {Object} changeMap - boolean map of affected URLs
	 */
	function _propagateDown(node, cache, callback, changeMap) {
		callback(node);
		changeMap[node.$URL] = true;

		_.each(cache.getNodes(node.Children), function(child) {
			_propagateDown(child, cache, callback, changeMap);
		});
	}

	/**
	 * When path is made Visible, then make sure all parents are visible as well. Add to the list of hiddenSiblings any siblings of items that remain
	 * hidden, as those are the ones that will need to be added explicitly as hidden paths.
	 * @param {Node} node
	 * @param {Cache} cache
	 * @param {string[]} visiblePaths - list of paths that need to be made visible
	 * @param {string[]} hiddenPaths - list of paths that need to be hidden
	 */
	function _propagateVisibleUp(node, cache, visiblePaths, hiddenPaths) {
		var parent = cache.getNode(node[xml.ATTR_PARENT]);
		if (!parent || parent.Visible) {
			return;
		}

		// find siblings of current path that will need to be hidden explicitly now that parent is now visible
		_.each(cache.getNodes(parent.Children), function(child) {
			if (!child[ATTR_VISIBLE] && (child != node)) {
				hiddenPaths.push(child);
			}
		});

		// add parent to list of paths that should now be visible
		visiblePaths.push(parent);

		// continue to next parent
		_propagateVisibleUp(parent, cache, visiblePaths, hiddenPaths);
	}

	/**
	 * Recalculate values for path name attributes.
	 * @param {Node} node
	 */
	function _setPathName(node) {
		var loc = localStore.editLocale;
		var lr = Locale.getConfigResolver(loc);
		var path = node._data.path;
		var pathName = configPreferences.getPathName(path, lr);
		if (!pathName) {
			// if in development (isDebug) and not loading merged config and the current locale matches the locale being configured,
			// then the currently loaded resolver can act as the base resolver
			if (config.isDebug && !sessionStore.mergedLocale && (loc == sessionStore.locale)) {
				lr = locale.localeResolver;
			} else {
				lr = Locale.getBaseResolver(loc);
			}

			pathName = configPreferences.getPathName(path, lr);
		}

		xml.nodeCreateClientAttr(node, ATTR_PATHNAME, xml.ATTR_STRING, pathName);
		xml.nodeCreateClientAttr(node, ATTR_PATHNAME_INHERITED, xml.ATTR_BOOLEAN, pathName && !configPreferences.isPathNameExplicit(path, lr));
	}

	/**
	 * Recalculate value for Visible attribute and if editable or not.
	 * @param {Node} node
	 */
	function _setVisible(node) {
		var path = node._data.path;
		var hiddenFromStart = _pathHints.isPathHiddenFromStartType(path);
		var hiddenIndirectly = _pathHints.isPathHidden(path, true);
		xml.nodeCreateClientAttr(node, ATTR_VISIBLE, xml.ATTR_BOOLEAN, !hiddenFromStart && !hiddenIndirectly, hiddenIndirectly ? xml.ATTR_FLAG_NOEDIT : 0);
	}

	var _PathNameController = declare(TextController, {
		/**
		 * Save path name to localization.
		 */
		toWriter: function() {
			this.oldValue = this.oldValue || "";
			this.newValue = this.newValue || "";

			if (this.oldValue != this.newValue) {
				var node = this.getNode();
				var path = node._data.path;
				var resourceAttr = locale.localeResolver.statics.TYPE_PATH;

				Locale.setResource(this.m_writer, Locale.ATTR_TYPES, [path.getStartType(), resourceAttr, path.getStandardPath()], this.newValue, localStore.editLocale);

				// no need to return results of change
				this.m_writer.noReturnNodes();
			}
		},

		/**
		 * Update children path names.
		 */
		postProcessSuccess: function() {
			var view = this.widget;
			var changeMap = {};

			_propagateDown(this.getNode(), this.m_cache, _setPathName, changeMap);

			view.refreshDataRows(function(tr) {
				return changeMap[view.getRefNodeUrl(tr)];
			});
		}
	});

	var _VisibleController = declare(BooleanController, {
		/**
		 * Propagate change to parents, writing changes to SitePreferences.HiddenPaths
		 */
		toWriter: function() {
			var cn = this.m_writer.changeNode(reflection.URL_SITE_PREFERENCES);
			var node = this.getNode();
			var hiddenPaths = [];
			var visiblePaths = [];

			if (this.newValue) {
				visiblePaths.push(node);
				_propagateVisibleUp(node, this.m_cache, visiblePaths, hiddenPaths);
			} else {
				hiddenPaths.push(node);
			}

			var removedPaths = [];

			_.each(visiblePaths, function(n) {
				n[ATTR_VISIBLE] = true;
				_pathHints.removeHiddenPath(n._data.path, removedPaths);
			});

			var addedPaths = _.map(hiddenPaths, function(n) {
				n[ATTR_VISIBLE] = false;
				_pathHints.addHiddenPath(n._data.path, removedPaths);
				return n._data.path.toString();
			});

			cn.changeAttribute("HiddenPaths", xml.ATTR_STRINGLIST, removedPaths, false, true);
			cn.changeAttribute("HiddenPaths", xml.ATTR_STRINGLIST, addedPaths, true, false);
		},

		/**
		 * Update children visible status and refresh parents and children
		 */
		postProcessSuccess: function() {
			var view = this.widget;
			var changeMap = {};

			// update visible status on paths longer than changed path
			_propagateDown(this.getNode(), this.m_cache, _setVisible, changeMap);

			// refresh parent and child rows
			view.postActionRefreshParentRows(this);

			view.refreshDataRows(function(tr) {
				return changeMap[view.getRefNodeUrl(tr)];
			});
		}
	});

	return declare(Widget, {
		disableCustomViewToolbar: true,
		plugins: [],

		_actionControllers: {
			"EditPathName": _PathNameController,
			"EditVisible": _VisibleController
		},

		/**
		 * Wait for resolver to load for current edit locale
		 */
		onBuildDeferreds: function(ctx, dfds) {
			dfds.push(Locale.loadEditResolvers());
		},

		/**
		 * Initialize members and globals
		 */
		postMixInProperties: function() {
			var typesStore = _getTypeStore();

			_selectedType = _selectedType || typesStore.query(null, {start: 0, count: 1})[0].id;

			this.defView._addViewAction(ACTION_ABSTRACT, {
				materialIcon: "gps_fixed",
				m_typeControl: def.ACTION_ICON,
				m_accessAction: "_accessActionAbstract",
				m_onAction: "_onActionAbstract"
			});

			this.defView.defToolbar = lang.mixin(new Toolbar(), {
				m_actions: [
					lang.mixin(new Action(), {
						m_id: "ViewConfigSelectType",
						m_onAction: "_onType",
						classToolbarPlugin: ToolbarPluginSelect,
						classToolbarPluginParams: {
							showLabel: true,
							store: typesStore,
							value: _selectedType
						}
					}),
					lang.mixin(new Action(), {
						m_id: "SwitchLocale",
						classToolbarPlugin: ToolbarPluginLocalizedItem
					})
				]
			});

			if (config.isDebug) {
				this.defView.defToolbar.m_actions.push(lang.mixin(new Action(), {
					classController: "Debug/PathCheck",
					m_id: "Compare Path Names"
				}));
			}

			_pathHints = new PathHints();
			_pathHints.addHiddenPathsFromPreferences(sessionStore.sitePreferences);

			this.inherited(arguments);
		},

		/**
		 * Return true if target class is abstract
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		_accessActionAbstract: function(ctx, action) {
			var node = ctx.getNode();
			var target = node[ATTR_TARGET];
			var rcTarget = reflection.getClass(target);
			return rcTarget.abstract;
		},

		/**
		 * Configure query level
		 */
		_createQueryLevel: function(params, bRoot) {
			var ql = this.inherited(arguments);
			if (ql.bMain) {
				ql.bReadOnly = !sessionConstants.configEditor;
				ql.createCustomStore = _getStore;
				ql.hasChildrenTest = _hasChildren;

				if (sessionStore.debugMode || config.isDebug) {
					ql.actions = lang.mixin(new Actions(), {
						m_actions: [this.getAction(ACTION_ABSTRACT)]
					});
				}
			}

			return ql;
		},

		/**
		 * Output possible api statements to console to resolve abstract path
		 */
		_onActionAbstract: function(ac) {
			var qr = this.getQueryResultByDomNode(ac.m_element);
			var node = qr.getNode();
			var target = node[ATTR_TARGET];
			var rcTarget = reflection.getClass(target);
			var ep = node._data.path.clone();

			_.each(rcTarget.getConcreteClasses(), function(rc) {
				ep.setEndType(rc.getType());
				console.warn('api.addPathHint("' + ep.toString() + '")');
			});
		},

		/**
		 * Switch to paths of selected type
		 */
		_onType: function(ac) {
			_selectedType = ac.actionValue;
			this.refreshData();
		}
	});
});
